import React, { useMemo, useState } from 'react';

import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { SitrepCardSurface } from 'components/Reports/reports.types';
import ReadOnlyReportsSectionDisplay from 'components/Reports/ReadOnlyReportsSectionDisplay';
import ReportsFileAttachments from 'components/Reports/ReportsFileAttachments';
import SitrepCardActionsMenu from 'components/Reports/SitrepCardActionsMenu';
import SitrepCardShell from 'components/Reports/SitrepCardShell';
import SitrepDocumentView from 'components/Reports/document/SitrepDocumentView';
import {
  ReportContentSearch,
  sectionMatchesReportSearch,
} from 'components/Reports/ReportContentSearch';
import { nestSitrepSections } from 'components/Reports/document/nestSitrepSections';
import { ReportPresentation } from 'components/Reports/presentation/ReportPresentation';
import SitrepSectionTabs from 'components/Reports/SitrepSectionTabs';
import useSitrepCardContentData from 'components/Reports/hooks/useSitrepCardContentData';
import { useSectionAutoRefresh } from 'components/Reports/hooks/useSectionAutoRefresh';
import { useSitrepLayoutMode } from 'components/Reports/hooks/useSitrepLayoutMode';
import useSitrepSectionSelector from 'components/Reports/hooks/useSitrepSectionSelector';
import {
  SitrepDynamicDataProvider,
  SitrepSubmittedDataTabs,
} from 'components/Reports/section/SitrepDynamicDataContext';
import { sectionRendersSnapshotToggle } from 'components/Reports/section/dynamicSectionSnapshot';

type SitrepSectionPanelProps = {
  sections: SitrepSectionType[] | undefined;
  activeSectionIndex: number | null;
  onSectionChange: (index: number) => void;
  tabSetId: string;
  activeSection: SitrepSectionType | null;
  children: React.ReactNode;
};

export const SitrepSectionPanel: React.FC<SitrepSectionPanelProps> = ({
  sections,
  activeSectionIndex,
  onSectionChange,
  tabSetId,
  activeSection,
  children,
}) => {
  if (!sections) return null;

  return (
    <>
      <SitrepSectionTabs
        sections={sections}
        activeSectionIndex={activeSectionIndex}
        onSectionChange={onSectionChange}
        className="mb-3"
        idBase={tabSetId}
      />

      {activeSection && (
        <div
          id={`${tabSetId}-panel-${activeSectionIndex}`}
          role="tabpanel"
          aria-labelledby={`${tabSetId}-tab-${activeSectionIndex}`}
          className="reports-card__section-content"
        >
          {children}
        </div>
      )}
    </>
  );
};

type SitrepCardContentProps = {
  sitrep: GetSitrepsResponse;
  surface: SitrepCardSurface;
  hasInProgressDraft?: boolean;
  headerBanner?: React.ReactNode;
  children?: React.ReactNode;
};

/**
 * Shared internal component for displaying SITREP card content. Used by both
 * CurrentReportsSitrepCard and ReadOnlySitrepCard.
 */
const SitrepCardContent: React.FC<SitrepCardContentProps> = ({
  sitrep,
  surface,
  hasInProgressDraft = false,
  headerBanner,
  children,
}) => {
  const tabSetId = React.useId();
  const cardData = useSitrepCardContentData(sitrep.id);
  const { sitrepData, sectionsLoading, refetch } = cardData;
  const [presentation, setPresentation] = useState<'off' | 'document' | 'quad'>(
    'off'
  );
  const nestedSections = useMemo(
    () => nestSitrepSections(sitrepData?.sections ?? []),
    [sitrepData?.sections]
  );
  const topLevelSections = useMemo(
    () => nestedSections.map(({ children: _c, ...section }) => section),
    [nestedSections]
  );
  const [reportQuery, setReportQuery] = useState('');
  const visibleTopLevel = useMemo(
    () =>
      topLevelSections.filter((section) =>
        sectionMatchesReportSearch(
          section,
          reportQuery,
          nestedSections.find((item) => item.id === section.id)?.children ?? []
        )
      ),
    [nestedSections, reportQuery, topLevelSections]
  );
  const { activeSection, activeSectionIndex, setActiveSectionIndex } =
    useSitrepSectionSelector(visibleTopLevel);
  const activeChildSections = useMemo(() => {
    if (!activeSection) return [];
    return (
      nestedSections.find((s) => s.id === activeSection.id)?.children ?? []
    );
  }, [activeSection, nestedSections]);
  const { layoutMode, setLayoutMode } = useSitrepLayoutMode(sitrep, sitrepData);
  useSectionAutoRefresh({
    sitrepId: sitrep.id,
    sections: sitrepData?.sections,
  });

  return (
    <SitrepCardShell
      sitrep={sitrep}
      surface={surface}
      cardData={cardData}
      headerBanner={headerBanner}
      collapsible={surface === SitrepCardSurface.Historical}
      headerRight={
        <div onClick={(e) => e.stopPropagation()}>
          <SitrepCardActionsMenu
            sitrep={sitrep}
            layoutMode={layoutMode}
            onLayoutModeChange={setLayoutMode}
            retireSitrep={
              surface === SitrepCardSurface.Current ? sitrep : undefined
            }
            presentDisabled={sectionsLoading}
            onPresent={() => setPresentation('document')}
            onQuadChart={() => setPresentation('quad')}
            showVersionActions={surface === SitrepCardSurface.Current}
            hasInProgressDraft={hasInProgressDraft}
          />
        </div>
      }
    >
      <SitrepDynamicDataProvider
        approvalStatus={sitrep.approval_status ?? 'APPROVED'}
        sections={sitrepData?.sections}
      >
        {presentation !== 'off' ? (
          <ReportPresentation
            sitrep={{
              ...sitrep,
              approval_status: sitrep.approval_status ?? 'APPROVED',
            }}
            sections={sitrepData?.sections ?? []}
            canEdit={false}
            layout={presentation}
            onRefetch={() => refetch()}
            onExit={() => setPresentation('off')}
            enableLiveAnalyticsRefresh={surface === SitrepCardSurface.Current}
          />
        ) : null}
        {layoutMode === 'document' ? (
          <SitrepDocumentView
            sitrep={{
              ...sitrep,
              approval_status: sitrep.approval_status ?? 'APPROVED',
            }}
            sections={sitrepData?.sections ?? []}
            canEdit={false}
            onRefetch={() => refetch()}
            enableLiveAnalyticsRefresh={surface === SitrepCardSurface.Current}
            footer={
              <div className="mt-3">
                <ReportsFileAttachments
                  sitrepId={sitrep.id}
                  workspaceId={sitrep.incident_id}
                  readOnly
                />
              </div>
            }
          />
        ) : (
          <>
            {topLevelSections.length > 0 ? (
              <ReportContentSearch
                value={reportQuery}
                onChange={setReportQuery}
              />
            ) : null}
            {reportQuery.trim() && visibleTopLevel.length === 0 ? (
              <p className="tw:mb-3 tw:text-sm tw:text-muted-foreground">
                No sections match that search.
              </p>
            ) : null}
            <SitrepSectionPanel
              sections={visibleTopLevel}
              activeSectionIndex={activeSectionIndex}
              onSectionChange={setActiveSectionIndex}
              tabSetId={tabSetId}
              activeSection={activeSection}
            >
              {sectionRendersSnapshotToggle(
                activeSection,
                activeChildSections
              ) ? (
                <SitrepSubmittedDataTabs />
              ) : null}
              {activeChildSections.length > 0 ? (
                <div className="tw:space-y-4">
                  {activeChildSections.map((child) => (
                    <div key={child.id}>
                      <h6 className="tw:mb-2 tw:text-sm tw:font-semibold">
                        {child.name}
                      </h6>
                      <ReadOnlyReportsSectionDisplay
                        section={child}
                        sitrepId={sitrep.id}
                        sitrep={{
                          ...sitrep,
                          approval_status: sitrep.approval_status ?? 'APPROVED',
                        }}
                        enableLiveAnalyticsRefresh={
                          surface === SitrepCardSurface.Current
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <ReadOnlyReportsSectionDisplay
                  section={activeSection!}
                  sitrepId={sitrep.id}
                  sitrep={{
                    ...sitrep,
                    approval_status: sitrep.approval_status ?? 'APPROVED',
                  }}
                  enableLiveAnalyticsRefresh={
                    surface === SitrepCardSurface.Current
                  }
                />
              )}
            </SitrepSectionPanel>
            <div className="mt-3">
              <ReportsFileAttachments
                sitrepId={sitrep.id}
                workspaceId={sitrep.incident_id}
                readOnly
              />
            </div>
          </>
        )}

        {children}
      </SitrepDynamicDataProvider>
    </SitrepCardShell>
  );
};

type ReadOnlySitrepCardProps = {
  sitrep: GetSitrepsResponse;
};

/**
 * Read-only SITREP card without routing dependencies. Use this in contexts
 * without router (e.g., map popups, embeds). Renders sections directly without
 * the full card shell.
 */
export const ReadOnlySitrepCard: React.FC<ReadOnlySitrepCardProps> = ({
  sitrep,
}) => {
  const tabSetId = React.useId();
  const cardData = useSitrepCardContentData(sitrep.id);
  const { sitrepData, sectionsLoading } = cardData;
  const nestedSections = useMemo(
    () => nestSitrepSections(sitrepData?.sections ?? []),
    [sitrepData?.sections]
  );
  const topLevelSections = useMemo(
    () => nestedSections.map(({ children: _c, ...section }) => section),
    [nestedSections]
  );
  const { activeSection, activeSectionIndex, setActiveSectionIndex } =
    useSitrepSectionSelector(topLevelSections);
  const activeChildSections = useMemo(() => {
    if (!activeSection) return [];
    return (
      nestedSections.find((s) => s.id === activeSection.id)?.children ?? []
    );
  }, [activeSection, nestedSections]);

  if (sectionsLoading) {
    return <div className="p-3">Loading SITREP details...</div>;
  }

  return (
    <SitrepDynamicDataProvider
      approvalStatus={sitrep.approval_status ?? 'APPROVED'}
      sections={sitrepData?.sections}
    >
      <div className="reports-card__content">
        <h6 className="mb-3">
          {sitrep.status === 'Summarized' ? 'Current SITREP' : sitrep.status}
        </h6>

        <SitrepSectionPanel
          sections={topLevelSections}
          activeSectionIndex={activeSectionIndex}
          onSectionChange={setActiveSectionIndex}
          tabSetId={tabSetId}
          activeSection={activeSection}
        >
          {sectionRendersSnapshotToggle(activeSection, activeChildSections) ? (
            <SitrepSubmittedDataTabs />
          ) : null}
          {activeChildSections.length > 0 ? (
            <div className="tw:space-y-4">
              {activeChildSections.map((child) => (
                <div key={child.id}>
                  <h6 className="tw:mb-2 tw:text-sm tw:font-semibold">
                    {child.name}
                  </h6>
                  <ReadOnlyReportsSectionDisplay
                    section={child}
                    sitrepId={sitrep.id}
                    sitrep={{
                      ...sitrep,
                      approval_status: sitrep.approval_status ?? 'APPROVED',
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <ReadOnlyReportsSectionDisplay
              section={activeSection!}
              sitrepId={sitrep.id}
              sitrep={{
                ...sitrep,
                approval_status: sitrep.approval_status ?? 'APPROVED',
              }}
            />
          )}
        </SitrepSectionPanel>

        <div className="mt-3">
          <ReportsFileAttachments
            sitrepId={sitrep.id}
            workspaceId={sitrep.incident_id}
            readOnly
          />
        </div>
      </div>
    </SitrepDynamicDataProvider>
  );
};

type CurrentReportsSitrepCardProps = {
  sitrep: GetSitrepsResponse;
  /**
   * Historical reports omit Draft New Version and auto-refresh. Those stay on
   * the Current report kebab.
   */
  hideDraftButton?: boolean;
  hasInProgressDraft?: boolean;
  headerBanner?: React.ReactNode;
};

const CurrentReportsSitrepCard: React.FC<CurrentReportsSitrepCardProps> = ({
  sitrep,
  hideDraftButton = false,
  hasInProgressDraft = false,
  headerBanner,
}) => {
  const surface = hideDraftButton
    ? SitrepCardSurface.Historical
    : SitrepCardSurface.Current;

  return (
    <SitrepCardContent
      sitrep={sitrep}
      surface={surface}
      hasInProgressDraft={hasInProgressDraft}
      headerBanner={headerBanner}
    />
  );
};

export default CurrentReportsSitrepCard;
